import React, { Component } from 'react';
import Router from 'next/router';
import { withErrorBoundary } from '@/utils/errors';
import { setCookie, parseCookies, destroyCookie } from '@/lib/cookies';
import isObject from 'isobject';
import { getDisplayName, isAbortError } from '@/utils/helpers';
import { getLoginStack } from '@/utils/storage';
import { config } from '@/config';
import { getEnv } from '@/lib/env';

const auth = ctx => {
  const { token } = parseCookies(ctx);
  const info = ctx.store.getState()?.currentAccount?.info;
  const billingInfoError =
    !ctx.pathname.includes('/account/profile') &&
    !ctx.pathname.includes('billing') &&
    isObject(info) &&
    !info['paid_user?'];
  const changePasswordError = !ctx.pathname.includes('/account/profile') && isObject(info) && info['change_password?'];

  const currentUser = ctx.store.getState()?.currentUser;
  const twoFaRequiredError =
    !(
      ctx.pathname.includes('billing/plan') ||
      ctx.pathname.includes('billing/review') ||
      ctx.pathname.includes('billing/update') ||
      ctx.pathname.includes('/account/profile')
    ) &&
    isObject(currentUser) &&
    currentUser['two_fa_is_required?'] &&
    !currentUser['has_2fa_enabled?'] &&
    !(getLoginStack()
      ? getLoginStack().peek() === 'impersonating_as_rm' ||
        getLoginStack().peek() === 'impersonating' ||
        getLoginStack().peek() === 'logged_as_admin'
      : true);

  if (ctx.req) {
    if (!token) {
      ctx.res.statusCode = 302;
      ctx.res.setHeader('Location', '/login');
      setRedirectPath(ctx);
      return;
    }
    if (!currentUser.updated_tos) {
      ctx.res.statusCode = 302;
      return;
    }
    if (changePasswordError) {
      ctx.res.statusCode = 302;
      ctx.res.setHeader('Location', '/account/profile?tab=password');
      return;
    }
    if (twoFaRequiredError) {
      ctx.res.statusCode = 302;
      ctx.res.setHeader('Location', '/account/profile?tab=two_factor');
      return;
    }
    if (billingInfoError) {
      ctx.res.statusCode = 302;
      ctx.res.setHeader('Location', '/billing');
      return;
    }
  }
  if (!currentUser.updated_tos) {
    return;
  }
  if (changePasswordError) {
    return Router.push('/account/profile?tab=password');
  }
  if (twoFaRequiredError) {
    return Router.push('/account/profile?tab=two_factor');
  }
  if (billingInfoError) {
    return Router.push('/billing');
  }
};

export const XCSRFHeader = () => {
  return { 'X-CSRF-Token': parseCookies()?.['X-CSRF-Token'] || '' };
};

export const handleUnauthorizedError = async ({ err, ctx }) => {
  if (isAbortError(err)) return null;
  if (err?.response?.status === 401) {
    destroyCookie(ctx, 'token', {
      domain: getCookieDomain(ctx)
    });
    setRedirectPath(ctx);
    if (ctx.res) {
      ctx.res.statusCode = 302;
      ctx.res.setHeader('Location', '/login');
    } else {
      window.location = '/login';
    }
  } else {
    throw err;
  }
};

const setRedirectPath = ctx => {
  if (!['/contacts'].some(element => ctx.asPath.includes(element))) return;
  setCookie(ctx, 'redirect_path', ctx.asPath, {
    path: '/',
    domain: getCookieDomain(ctx)
  });
};

// Returns the path only if it is a same-origin relative path (begins with a single /,
// no protocol-relative //host or absolute URLs). Returns null for anything suspicious so
// callers can default to a safe internal route instead of blindly trusting the cookie.
export const sanitizeRedirectPath = path => {
  if (!path || typeof path !== 'string') return null;
  try {
    const appUrl = getEnv('NEXT_PUBLIC_APP_URL') || 'https://dummy.local';
    const base = new URL(appUrl);
    const url = new URL(path, base.origin);
    if (url.origin !== base.origin) return null;
    const safe = url.pathname + url.search + url.hash;
    return safe || '/';
  } catch {
    return null;
  }
};

const getCookieDomain = ctx => {
  if (config().IS_DEV) return undefined;

  // Get hostname from request (SSR) or window (client)
  const host = ctx?.req?.headers?.host || (typeof window !== 'undefined' ? window.location.host : '');
  const hostname = host.split(':')[0];

  // Extract base domain (e.g., app.example.com -> .example.com, example.com -> .example.com)
  const parts = hostname.split('.');
  return parts.length >= 2 ? `.${parts.slice(-2).join('.')}` : undefined;
};

export const withAuthSync = WrappedComponent => {
  const ComponentWithErrorBoundary = withErrorBoundary(WrappedComponent);
  return class extends Component {
    static displayName = `withAuthSync(${getDisplayName(WrappedComponent)})`;

    static async getInitialProps({ ctx, router }) {
      auth(ctx);

      const componentProps =
        ComponentWithErrorBoundary.getInitialProps &&
        (await ComponentWithErrorBoundary.getInitialProps({ ctx, router }));

      return { ...componentProps };
    }

    render() {
      return <ComponentWithErrorBoundary {...this.props} />;
    }
  };
};
